import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchCoverage, coverageKey } from "./coverageSlice";
import { normalizeMarketLatLng } from "../config/market";

/**
 * useCoverage — the single hook every web checkout surface uses to answer
 * "does THIS vendor deliver to the customer's set location?" from the
 * AUTHORITATIVE backend (`GET /api/coverage`).
 *
 * Drop-off comes from the location the customer set on the home page
 * (`state.userLocation`). The verdict is whatever the backend returned — the
 * same road-distance + radius decision the order-create 422 gate makes — so the
 * shop-entry banner, add-to-cart block, and cart gate can never disagree with
 * the final Confirm step.
 *
 * @param {object}  params
 * @param {string}  params.vendorId
 * @param {string}  [params.outletId]    the branch being ordered from, when any;
 *                  its own coordinates and radius are what get checked
 * @param {string}  [params.vendorType]  'inhouse' for Users-id vendors
 * @param {boolean} [params.enabled=true]
 * @returns {{
 *   deliverable: (boolean|null),   // true | false | null(undecidable)
 *   distanceKm: (number|null),
 *   radius: (number|null),
 *   deliveryCoverage: (string|null),
 *   coverageCountry: (string|null),
 *   reason: (string|null),         // IN_RANGE|OUT_OF_RANGE|NO_DROPOFF|VENDOR_COORDS_MISSING|...
 *   status: ('idle'|'loading'|'succeeded'|'failed'),
 *   hasLocation: boolean,          // did the customer set a location at all?
 *   isOutOfRange: boolean,         // deliverable === false (the hard-block signal)
 *   isChecking: boolean,
 *   refresh: () => void,
 * }}
 */
export default function useCoverage({
  vendorId,
  outletId = null,
  vendorType,
  enabled = true,
  lat: latOverride,
  lng: lngOverride,
} = {}) {
  const dispatch = useDispatch();
  const { latitude, longitude } = useSelector((state) => state.userLocation);
  // Callers can pin an explicit drop-off (e.g. the shipping screen checks the
  // ENTERED address, not the browse location) — otherwise use the location the
  // customer set on the home page (state.userLocation).
  const hasOverride = latOverride != null || lngOverride != null;
  const coordinates = normalizeMarketLatLng(
    hasOverride ? latOverride : latitude,
    hasOverride ? lngOverride : longitude
  );
  const latitudeUsed = coordinates?.lat;
  const longitudeUsed = coordinates?.lng;
  const hasLocation = Boolean(coordinates);

  const key = vendorId ? coverageKey(outletId || vendorId, latitudeUsed, longitudeUsed) : null;
  const entry = useSelector((state) => (key ? state.coverage.byKey[key] : null));

  // Only worth a backend call once we know both the vendor AND the drop-off.
  const canCheck = Boolean(enabled && vendorId && hasLocation);

  const refresh = useCallback(() => {
    if (!canCheck) return;
    dispatch(
      fetchCoverage({ vendorId, outletId, lat: latitudeUsed, lng: longitudeUsed, vendorType })
    );
  }, [dispatch, canCheck, vendorId, outletId, latitudeUsed, longitudeUsed, vendorType]);

  useEffect(() => {
    // Fetch once per (vendor, location) unless we already have/are getting it.
    if (!canCheck) return;
    // Failed requests are retried explicitly by the customer. Automatically
    // retrying them here creates a render/request loop during an outage.
    if (entry) return;
    refresh();
  }, [canCheck, entry, refresh]);

  const status = entry?.status || "idle";
  const deliverable = entry?.deliverable ?? null;

  return {
    deliverable,
    distanceKm: entry?.distanceKm ?? null,
    radius: entry?.radius ?? null,
    deliveryCoverage: entry?.deliveryCoverage ?? null,
    coverageCountry: entry?.coverageCountry ?? null,
    reason: entry?.reason ?? null,
    error: entry?.error ?? null,
    status,
    hasLocation,
    isOutOfRange: deliverable === false,
    isChecking: status === "loading",
    isConfirmed: status === "succeeded" && deliverable === true,
    isFailed: status === "failed",
    refresh,
  };
}
